import React, { Suspense } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './services/auth';
import MainLayout from './components/Layout/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';
import RouteMetadata from './components/RouteMetadata';

// 页面组件导入
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Contact from './pages/Contact';
// DeviceManagement 改为路由级懒加载
// 注意：lazy 声明需在所有 import 之后以避免 import/first 告警
import DeviceGroups from './pages/DeviceGroups';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import PolicyManagement from './pages/PolicyManagement';
const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const FRONTEND_CHUNK_RECOVERY_KEY = 'allowtru:chunk-recovery';
const reportFrontendDebug = (event, payload = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.__allowtruReportFrontendDebug === 'function') {
      window.__allowtruReportFrontendDebug(event, payload);
    }
  } catch (_) {}
};
const isChunkLoadError = (error) => {
  const name = String(error?.name || '').toLowerCase();
  const message = String(error?.message || error || '').toLowerCase();
  return name.includes('chunk') ||
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('unexpected token \'<\'');
};
const triggerChunkRecoveryReload = (chunk) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }
    const current = `${window.location.pathname}${window.location.search}`;
    const marker = `${chunk}@${current}`;
    if (window.sessionStorage.getItem(FRONTEND_CHUNK_RECOVERY_KEY) === marker) {
      return false;
    }
    window.sessionStorage.setItem(FRONTEND_CHUNK_RECOVERY_KEY, marker);
    reportFrontendDebug('agents_lazy_recovery_reload', {
      chunk,
      path: current,
    });
    window.location.reload();
    return true;
  } catch (_) {
    return false;
  }
};
const clearChunkRecoveryReload = (chunk) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    const current = `${window.location.pathname}${window.location.search}`;
    const marker = `${chunk}@${current}`;
    if (window.sessionStorage.getItem(FRONTEND_CHUNK_RECOVERY_KEY) === marker) {
      window.sessionStorage.removeItem(FRONTEND_CHUNK_RECOVERY_KEY);
    }
  } catch (_) {}
};
// 路由级懒加载：AgentDownload 页面（需在所有 import 之后，避免 ESLint import/first 告警）
const AgentDownload = React.lazy(() => import('./pages/AgentDownload')
  .then((mod) => {
    clearChunkRecoveryReload('AgentDownload');
    reportFrontendDebug('agents_lazy_loaded', {
      chunk: 'AgentDownload',
    });
    return mod;
  })
  .catch((error) => {
    reportFrontendDebug('agents_lazy_failed', {
      chunk: 'AgentDownload',
      error_message: error?.message || String(error),
      error_name: error?.name || '',
    });
    if (isChunkLoadError(error) && triggerChunkRecoveryReload('AgentDownload')) {
      return new Promise(() => {});
    }
    throw error;
  }));
const DeviceManagement = React.lazy(() => import('./pages/DeviceManagement'));
const DeviceActivityManagement = React.lazy(() => import('./pages/DeviceActivityManagement'));
const AuditLogManagement = IS_CUSTOMER_BUILD ? null : React.lazy(() => import('./pages/AuditLogManagement'));
const SupportTickets = IS_CUSTOMER_BUILD ? null : React.lazy(() => import('./pages/SupportTickets'));
const CommunicationSettings = IS_CUSTOMER_BUILD ? null : React.lazy(() => import('./pages/CommunicationSettings'));
const TenantManagement = IS_CUSTOMER_BUILD ? null : React.lazy(() => import('./pages/TenantManagement'));
const SUPPORT_AGENT_ROLE = IS_CUSTOMER_BUILD ? '' : ['support', 'agent'].join('_');
const SUPPORT_TICKETS_KEY = IS_CUSTOMER_BUILD ? '' : ['support', 'tickets'].join('_');
const SUPPORT_TICKETS_PATH = IS_CUSTOMER_BUILD ? '' : `/${['support', 'tickets'].join('-')}`;
const COMMUNICATION_SETTINGS_KEY = IS_CUSTOMER_BUILD ? '' : ['communication', 'settings'].join('_');
const COMMUNICATION_SETTINGS_PATH = IS_CUSTOMER_BUILD ? '' : '/support-center/communication-settings';
const DEVICE_ACTIVITY_KEY = 'device_activity_management';
const DEVICE_ACTIVITY_PATH = '/activities';
const LEGACY_DEVICE_ACTIVITY_PATH = '/logs';
const AUDIT_LOG_KEY = IS_CUSTOMER_BUILD ? '' : ['audit', 'log'].join('_');
const AUDIT_LOG_PATH = IS_CUSTOMER_BUILD ? '' : `/${['audit', 'logs'].join('-')}`;

const normalizeModuleToggles = (modules) => {
  if (!modules || typeof modules !== 'object') {
    return {};
  }
  const normalized = { ...modules };
  if (Object.prototype.hasOwnProperty.call(normalized, 'logs') && !Object.prototype.hasOwnProperty.call(normalized, DEVICE_ACTIVITY_KEY)) {
    normalized[DEVICE_ACTIVITY_KEY] = normalized.logs;
  }
  delete normalized.logs;
  return normalized;
};

const parseTenantSettings = (source) => {
  if (source?.modules && typeof source.modules === 'object') {
    return source.modules;
  }
  let modules = {};
  try {
    const tenant = source?.tenant || source;
    if (tenant?.settings) {
      const settings = typeof tenant.settings === 'string' ? JSON.parse(tenant.settings) : tenant.settings;
      if (settings && settings.modules && typeof settings.modules === 'object') {
        modules = settings.modules;
      }
    }
  } catch (e) {
    console.warn('parseTenantSettings failed:', e);
  }
  return modules;
};

const parseTenantFeatures = (source) => {
  const features = Array.isArray(source?.features)
    ? source.features
    : (Array.isArray(source?.tenant?.features) ? source.tenant.features : []);
  const tenantCode = typeof source?.tenant?.code === 'string' ? source.tenant.code.trim().toLowerCase() : '';
  if (tenantCode === 'default') {
    return features.length === 0 ? ['ALLOWTRU_AGENT'] : Array.from(new Set([...features, 'ALLOWTRU_AGENT']));
  }
  if (features.length === 0) {
    return ['ALLOWTRU_AGENT'];
  }
  return features;
};

const buildDefaultToggles = (role) => {
  const isSuper = role === 'super_admin' && !IS_CUSTOMER_BUILD;
  const isSupportAgent = !!SUPPORT_AGENT_ROLE && role === SUPPORT_AGENT_ROLE;
  const toggles = {
    dashboard: !isSupportAgent,
    devices: !isSupportAgent,
    device_groups: !isSupportAgent,
    policies: !isSupportAgent,
    [DEVICE_ACTIVITY_KEY]: !isSupportAgent,
    agents: !isSupportAgent,
    tenants: isSuper,
    settings: !isSupportAgent,
    [AUDIT_LOG_KEY]: !IS_CUSTOMER_BUILD && !isSupportAgent && (role === 'super_admin' || role === 'admin' || role === 'tenant_admin'),
    profile: !isSupportAgent,
  };
  if (SUPPORT_TICKETS_KEY) {
    toggles[SUPPORT_TICKETS_KEY] = isSuper || isSupportAgent;
  }
  if (COMMUNICATION_SETTINGS_KEY) {
    toggles[COMMUNICATION_SETTINGS_KEY] = isSuper;
  }
  return toggles;
};

const applyFeatureToggles = (toggles, tenant, role) => {
  if (role === 'super_admin') {
    return {
      ...toggles,
    };
  }
  const tenantModules = parseTenantSettings(tenant);
  const features = parseTenantFeatures(tenant);
  let hasAgent = features.includes('ALLOWTRU_AGENT');

  const hasModulesConfig = tenantModules && Object.keys(tenantModules).length > 0;
  if (hasModulesConfig) {
    const agentDisabledByModules =
      tenantModules.devices === false &&
      tenantModules.device_groups === false &&
      tenantModules.policies === false &&
      tenantModules.agents === false;

    if (agentDisabledByModules) {
      hasAgent = false;
    }
  }
  if (!hasAgent) {
    return {
      ...toggles,
      policies: false,
      [DEVICE_ACTIVITY_KEY]: false,
    };
  }
  return toggles;
};

const resolveToggles = (user) => {
  const role = user?.role?.toLowerCase() || '';
  const defaults = buildDefaultToggles(role);
  if (SUPPORT_AGENT_ROLE && role === SUPPORT_AGENT_ROLE) {
    return defaults;
  }
  const tenantModules = normalizeModuleToggles(parseTenantSettings(user));
  return applyFeatureToggles({ ...defaults, ...tenantModules }, user, role);
};

const getDefaultAuthorizedPath = (toggles) => {
  if (SUPPORT_TICKETS_PATH && toggles[SUPPORT_TICKETS_KEY] !== false) return SUPPORT_TICKETS_PATH;
  if (COMMUNICATION_SETTINGS_PATH && toggles[COMMUNICATION_SETTINGS_KEY] !== false) return COMMUNICATION_SETTINGS_PATH;
  if (toggles.dashboard !== false) return '/dashboard';
  if (toggles.devices !== false) return '/devices';
  if (toggles[DEVICE_ACTIVITY_KEY] !== false) return DEVICE_ACTIVITY_PATH;
  if (toggles[AUDIT_LOG_KEY] !== false) return AUDIT_LOG_PATH;
  if (toggles.settings !== false) return '/settings';
  return '/profile';
};

const isRouteAllowed = (path, toggles) => {
  const cleanPath = (path || '').split('?')[0];
  if (cleanPath === '/dashboard' || cleanPath.startsWith('/dashboard/')) return toggles.dashboard !== false;
  if (cleanPath === '/devices' || cleanPath.startsWith('/devices/')) return toggles.devices !== false;
  if (cleanPath === '/device-groups' || cleanPath.startsWith('/device-groups/')) return toggles.device_groups !== false;
  if (cleanPath === '/policies' || cleanPath.startsWith('/policies/')) return toggles.policies !== false;
  if (
    cleanPath === DEVICE_ACTIVITY_PATH ||
    cleanPath.startsWith(`${DEVICE_ACTIVITY_PATH}/`) ||
    cleanPath === LEGACY_DEVICE_ACTIVITY_PATH ||
    cleanPath.startsWith(`${LEGACY_DEVICE_ACTIVITY_PATH}/`)
  ) {
    return toggles[DEVICE_ACTIVITY_KEY] !== false;
  }
  if (cleanPath === AUDIT_LOG_PATH || cleanPath.startsWith(`${AUDIT_LOG_PATH}/`)) return toggles[AUDIT_LOG_KEY] !== false;
  if (cleanPath === '/agents' || cleanPath.startsWith('/agents/')) return toggles.agents !== false;
  if (SUPPORT_TICKETS_PATH && (cleanPath === SUPPORT_TICKETS_PATH || cleanPath.startsWith(`${SUPPORT_TICKETS_PATH}/`))) return toggles[SUPPORT_TICKETS_KEY] !== false;
  if (COMMUNICATION_SETTINGS_PATH && (cleanPath === COMMUNICATION_SETTINGS_PATH || cleanPath.startsWith(`${COMMUNICATION_SETTINGS_PATH}/`))) return toggles[COMMUNICATION_SETTINGS_KEY] !== false;
  if (!IS_CUSTOMER_BUILD && (cleanPath === '/tenants' || cleanPath.startsWith('/tenants/'))) return toggles.tenants !== false;
  if (cleanPath === '/settings' || cleanPath.startsWith('/settings/')) return toggles.settings !== false;
  if (cleanPath === '/profile' || cleanPath.startsWith('/profile/')) return toggles.profile !== false;
  return true;
};

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div>Loading...</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const toggles = resolveToggles(user);
  if (!isRouteAllowed(location.pathname, toggles)) {
    return <Navigate to={getDefaultAuthorizedPath(toggles)} replace />;
  }
  return children;
};

// 公共路由组件
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  if (user) {
    return <Navigate to={getDefaultAuthorizedPath(resolveToggles(user))} replace />;
  }
  return children;
};

// 应用路由
const AppRoutes = () => {
  return (
    <Routes>
      {/* 公共路由 */}
      <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
      <Route path="/agents" element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><AgentDownload /></Suspense></MainLayout></ProtectedRoute>} />
      {/* 删除：移除 /agents/install-config 独立页面路由 */}

      {/* 受保护路由 */}
      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><DeviceManagement /></Suspense></MainLayout></ProtectedRoute>} />
      <Route path="/device-groups" element={<ProtectedRoute><MainLayout><DeviceGroups /></MainLayout></ProtectedRoute>} />
      {!IS_CUSTOMER_BUILD && SupportTickets && SUPPORT_TICKETS_PATH ? (
        <Route path={SUPPORT_TICKETS_PATH} element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><SupportTickets /></Suspense></MainLayout></ProtectedRoute>} />
      ) : null}
      {!IS_CUSTOMER_BUILD && CommunicationSettings && COMMUNICATION_SETTINGS_PATH ? (
        <Route path={COMMUNICATION_SETTINGS_PATH} element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><CommunicationSettings /></Suspense></MainLayout></ProtectedRoute>} />
      ) : null}
      {!IS_CUSTOMER_BUILD && TenantManagement ? (
        <Route path="/tenants" element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><TenantManagement /></Suspense></MainLayout></ProtectedRoute>} />
      ) : null}
      <Route path="/policies" element={<ProtectedRoute><MainLayout><PolicyManagement /></MainLayout></ProtectedRoute>} />
      <Route path={DEVICE_ACTIVITY_PATH} element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><DeviceActivityManagement /></Suspense></MainLayout></ProtectedRoute>} />
      <Route path={LEGACY_DEVICE_ACTIVITY_PATH} element={<ProtectedRoute><Navigate to={DEVICE_ACTIVITY_PATH} replace /></ProtectedRoute>} />
      {!IS_CUSTOMER_BUILD && AuditLogManagement ? (
        <Route path={AUDIT_LOG_PATH} element={<ProtectedRoute><MainLayout><Suspense fallback={<div>Loading...</div>}><AuditLogManagement /></Suspense></MainLayout></ProtectedRoute>} />
      ) : null}
      <Route path="/settings" element={<ProtectedRoute><MainLayout><Settings /></MainLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><MainLayout><Profile /></MainLayout></ProtectedRoute>} />

      {/* 404 重定向 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const RoutedErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      {children}
    </ErrorBoundary>
  );
};

// 主应用组件
// 在旧内核或不支持 HTML5 History 的环境中，回退到 HashRouter
const supportsHistory = () => {
  try {
    return !!(window.history && window.history.pushState);
  } catch (e) {
    return false;
  }
};

const App = () => {
  const RouterComponent = supportsHistory() ? BrowserRouter : HashRouter;
  return (
    <RouterComponent>
      <RouteMetadata />
      <AuthProvider>
        <RoutedErrorBoundary>
          <AppRoutes />
        </RoutedErrorBoundary>
      </AuthProvider>
    </RouterComponent>
  );
};

export default App;
